<template>
  <div class="main-block">
    <!-- Preloader overlay -->
    <div v-if="isNavigating" class="lot-navigation-preloader">
      <preloader></preloader>
    </div>

    <!-- Navigation arrow left -->
    <button
      v-if="hasPrevLot"
      class="lot-nav-arrow lot-nav-arrow-left swiper-navigation-btn"
      @click="navigateToPrevLot"
      :disabled="isNavigating"></button>

    <div class="main-lot-image-block">
      <swiper
        v-if="files?.length > 1"
        class="images-swiper-navigation"
        direction="vertical"
        :slides-per-view="'auto'"
        :navigation="true"
        type="lotGallery">
        <swiper-slide class="navigation-image" v-for="(file, index) in files" :key="index">
          <img v-if="file.type === LotFileTypes.IMAGE" :src="file.src" alt="lot" />
          <img
            v-else-if="file.type === LotFileTypes.PDF"
            :src="auctionStore.filePlaceholders.pdf_min"
            class="mini-video-placeholder"
            alt="lot" />
          <img
            v-else-if="file.type === LotFileTypes.VIDEO"
            :src="auctionStore.filePlaceholders.video"
            class="mini-video-placeholder"
            alt="lot" />
          <img v-else :src="auctionStore.filePlaceholders.no_image" alt="lot" />
        </swiper-slide>
      </swiper>

      <div class="main-lot-image">
        <swiper
          thumbs-swiper=".images-swiper-navigation .swiper-wrapper"
          :slides-per-view="1"
          :navigation="false"
          :allow-touch-move="false"
          type="lotGalleryMain">
          <swiper-slide class="swiper-slide" v-for="(file, index) in files" :key="index">
            <template v-if="file.type === LotFileTypes.IMAGE">
              <div class="magnifier-zoom-image">
                <img class="magnifier-image" :src="file.src" alt="lot" />
                <div class="magnifier-zone" :data-magnifier-src="file.src"></div>
              </div>
            </template>
            <template v-else-if="file.type === LotFileTypes.PDF">
              <a :href="file.src" class="magnifier-zoom-image" target="_blank">
                <img :src="auctionStore.filePlaceholders.pdf" alt="lot" class="pdf-placeholder" />
              </a>
            </template>
            <template v-else-if="file.type === LotFileTypes.VIDEO">
              <video-player-custom
                class="custom-video-player magnifier-zoom-image"
                :video-src="file.src"
                title="lot"></video-player-custom>
            </template>
            <template v-else>
              <img class="magnifier-image" :src="auctionStore.filePlaceholders.no_image" alt="lot" />
            </template>
          </swiper-slide>
        </swiper>
      </div>
    </div>

    <card-info-block v-if="lot?.id" @close-form="emit('closeForm')"></card-info-block>

    <!-- Navigation arrow right -->
    <button
      v-if="hasNextLot"
      class="lot-nav-arrow lot-nav-arrow-right swiper-navigation-btn"
      @click="navigateToNextLot"
      :disabled="isNavigating"></button>
  </div>
</template>

<script lang="ts" setup>
  import { computed, onMounted, onUnmounted, ref, watch } from "vue";
  import Swiper from "@/components/_partials/Swiper.vue";
  import VideoPlayerCustom from "@/components/_partials/VideoPlayerCustom.vue";
  import CardInfoBlock from "@/components/CardInfoBlock.vue";
  import Preloader from "@/components/_partials/Preloader.vue";
  import { LotFileTypes } from "@/enum";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import { ILotPopupData } from "@/models";
  import { isObject } from "lodash";

  const emit = defineEmits(["closeForm", "changeTitle", "setModalHeightAuto"]);

  const auctionStore = useAuctionStore();
  const lot = computed(() => auctionStore.popupLot as ILotPopupData);
  const files = computed(() => {
    return isObject(lot.value?.files) ? Object.values(lot.value?.files) || [] : [];
  });

  const props = defineProps<{
    lotProp: ILotPopupData;
    allLots?: any[];
    currentLotIndex?: number;
    onNavigateToLot?: (lot: any, index: number) => void;
  }>();

  const isNavigating = ref(false);
  const currentLotId = ref<number | null>(null);

  const hasPrevLot = computed(() => {
    return props.allLots && props.currentLotIndex !== undefined && props.currentLotIndex > 0;
  });

  const hasNextLot = computed(() => {
    return props.allLots && props.currentLotIndex !== undefined && props.currentLotIndex < props.allLots.length - 1;
  });

  const navigateToPrevLot = () => {
    if (hasPrevLot.value && !isNavigating.value && props.onNavigateToLot && props.allLots && props.currentLotIndex !== undefined) {
      isNavigating.value = true;
      const prevLot = props.allLots[props.currentLotIndex - 1];
      props.onNavigateToLot(prevLot, props.currentLotIndex - 1);
    }
  };

  const navigateToNextLot = () => {
    if (hasNextLot.value && !isNavigating.value && props.onNavigateToLot && props.allLots && props.currentLotIndex !== undefined) {
      isNavigating.value = true;
      const nextLot = props.allLots[props.currentLotIndex + 1];
      props.onNavigateToLot(nextLot, props.currentLotIndex + 1);
    }
  };

  // Watch for lotProp changes and update store
  watch(
    () => props.lotProp,
    (newLot) => {
      if (newLot) {
        auctionStore.$patch((state) => {
          state.popupLot = newLot;
        });
        // Hide preloader when lot data is updated
        if (isNavigating.value && newLot.id !== currentLotId.value) {
          currentLotId.value = newLot.id;
          isNavigating.value = false;
        }
      }
    },
    { immediate: true }
  );

  onMounted(() => {
    auctionStore.$patch((state) => {
      state.popupLot = props.lotProp;
    });
    currentLotId.value = props.lotProp?.id || null;
  });

  onUnmounted(() => {
    auctionStore.$patch((state) => {
      state.popupLot = null;
    });
  });
</script>

<style scoped>
  .main-block {
    position: relative;
  }
</style>
